import { Alert, Box, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import type { EnhancedSelectOption } from "@/types/ui/component-props";

/**
 * Props for encounter details section.
 * @agentic connections
 */
export type EncounterDetailsSectionProps = {
  periodStart: string;
  periodEnd: string;
  encounterMethod: string;
  encounterDirection: string;
  encounterBillable: string;
  periodStartError?: string;
  periodEndError?: string;
  encounterMethodError?: string;
  encounterDirectionError?: string;
  encounterBillableError?: string;
  methodOptions: EnhancedSelectOption[];
  directionOptions: EnhancedSelectOption[];
  billableOptions: EnhancedSelectOption[];
  requiresBillable: boolean;
  readOnly?: boolean;
  formatDateTimeForInput: (value: string) => string;
  formatInputForDateTime: (value: string) => string;
  onPeriodStartChange: (value: string) => void;
  onPeriodEndChange: (value: string) => void;
  onMethodChange: (value: string) => void;
  onDirectionChange: (value: string) => void;
  onBillableChange: (value: string) => void;
};

/**
 * Interaction details section containing timing/method/direction/billable fields.
 * @agentic connections
 */
export const EncounterDetailsSection = ({
  periodStart,
  periodEnd,
  encounterMethod,
  encounterDirection,
  encounterBillable,
  periodStartError,
  periodEndError,
  encounterMethodError,
  encounterDirectionError,
  encounterBillableError,
  methodOptions,
  directionOptions,
  billableOptions,
  requiresBillable,
  readOnly = false,
  formatDateTimeForInput,
  formatInputForDateTime,
  onPeriodStartChange,
  onPeriodEndChange,
  onMethodChange,
  onDirectionChange,
  onBillableChange,
}: EncounterDetailsSectionProps) => (
  <>
    <FormSection title="Timing" showDivider={false}>
      <FormGroup>
        <FormField
          label="Start Time"
          required
          readOnly={readOnly}
          error={periodStartError}
        >
          <TextInput
            type="datetime-local"
            value={periodStart ? formatDateTimeForInput(periodStart) : ""}
            onChange={(e) => {
              const value = e.target.value;
              onPeriodStartChange(value ? formatInputForDateTime(value) : "");
            }}
          />
        </FormField>
        <FormField
          label="End Time"
          required
          readOnly={readOnly}
          error={periodEndError}
        >
          <TextInput
            type="datetime-local"
            value={periodEnd ? formatDateTimeForInput(periodEnd) : ""}
            onChange={(e) => {
              const value = e.target.value;
              onPeriodEndChange(value ? formatInputForDateTime(value) : "");
            }}
          />
        </FormField>
      </FormGroup>
    </FormSection>

    <FormField
      label="Method"
      required
      variant="section"
      showDivider={false}
      readOnly={readOnly}
      error={encounterMethodError}
    >
      <CardSelect
        placeholder="Select method"
        value={encounterMethod || ""}
        onChange={(value) => {
          const singleValue = Array.isArray(value) ? value[0] : value;
          onMethodChange(singleValue || "");
        }}
        columns={2}
        data={methodOptions}
        error={!!encounterMethodError}
      />
    </FormField>

    <FormField
      label="Direction"
      required
      variant="section"
      showDivider={false}
      readOnly={readOnly}
      error={encounterDirectionError}
    >
      <CardSelect
        placeholder="Select direction"
        value={encounterDirection || ""}
        onChange={(value) => {
          const singleValue = Array.isArray(value) ? value[0] : value;
          onDirectionChange(singleValue || "");
        }}
        columns={2}
        data={directionOptions}
        error={!!encounterDirectionError}
      />
    </FormField>

    {requiresBillable && (
      <FormField
        label="Billable Status"
        variant="section"
        showDivider={false}
        required
        error={encounterBillableError}
      >
        <Box mb="md">
          <Alert
            color="blue"
            radius="md"
            variant="light"
            icon={<IconInfoCircle size={18} />}
          >
            <Text fw={600} size="sm" mb={4}>
              This intake should be marked "Billable" only if the patient has a
              MA insurance coverage.
            </Text>
            <Text size="sm" c="dimmed" mb={2}>
              Federal, Medicare or out of state plans should be marked as
              non-billable.
            </Text>
            <Text size="sm" c="dimmed">
              If the patient agrees to CoCM, ensure this note is logged in the
              provider EMR.
            </Text>
          </Alert>
        </Box>
        <CardSelect
          placeholder="Select billable status"
          value={encounterBillable || ""}
          onChange={(value) => {
            const singleValue = Array.isArray(value) ? value[0] : value;
            onBillableChange(singleValue || "");
          }}
          data={billableOptions}
          columns={2}
          error={!!encounterBillableError}
        />
      </FormField>
    )}
  </>
);
